import React from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FilterButton, StyledActionButton, StyledSegmentedControl, StyledSegmentButton } from './SectionBar.styled';

interface CN56ReportFiltersProps {
  customFilters: {
    serviceTypes?: string[];
    groupBy?: string[];
    fromPeriod?: string;
    toPeriod?: string;
    direction?: string;
    onServiceTypesChange?: (values: string[]) => void;
    onGroupByChange?: (values: string[]) => void;
    onFromPeriodChange?: (period: string) => void;
    onToPeriodChange?: (period: string) => void;
    onDirectionChange?: (direction: string) => void;
    onExport?: (format: 'HTML' | 'PDF' | 'XLS') => void;
    isExporting?: boolean;
    yearQuarterOptions?: Array<{ value: string; label: string; year: string; quarter: string }>;
  };
}

const CN56ReportFilters: React.FC<CN56ReportFiltersProps> = ({ customFilters }) => {
  // State for dropdown menus
  const [serviceTypesAnchorEl, setServiceTypesAnchorEl] = React.useState<null | HTMLElement>(null);
  const [groupByAnchorEl, setGroupByAnchorEl] = React.useState<null | HTMLElement>(null);
  const [fromPeriodAnchorEl, setFromPeriodAnchorEl] = React.useState<null | HTMLElement>(null);
  const [toPeriodAnchorEl, setToPeriodAnchorEl] = React.useState<null | HTMLElement>(null);

  const isServiceTypesMenuOpen = Boolean(serviceTypesAnchorEl);
  const isGroupByMenuOpen = Boolean(groupByAnchorEl);
  const isFromPeriodMenuOpen = Boolean(fromPeriodAnchorEl);
  const isToPeriodMenuOpen = Boolean(toPeriodAnchorEl);

  // Generate year-quarter options for CN56Report (from 2008 to current year)
  // Use cascaded options from parent if provided, otherwise generate all options
  const yearQuarterOptions = React.useMemo(() => {
    // If parent provides cascaded options, use them
    if (customFilters.yearQuarterOptions && customFilters.yearQuarterOptions.length > 0) {
      return customFilters.yearQuarterOptions;
    }
    
    // Otherwise, generate all options
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year >= 2008; year--) { // Reverse order to show recent years first
      quarters.forEach(quarter => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({
          value,
          label,
          year: year.toString(),
          quarter
        });
      });
    }
    
    return options;
  }, [customFilters.yearQuarterOptions]);

  // Filter options
  const serviceTypeOptions = [
    { id: 'A', label: 'A - Airmail Priority' },
    { id: 'B', label: 'B - SAL Non-Priority' },
    { id: 'C', label: 'C - Surface Non-Priority' },
    { id: 'D', label: 'D - Surface Priority' },
  ];

  const groupByOptions = [
    { id: 'ms', label: 'Mail Subclass' },
    { id: 'out', label: 'OE From' },
    { id: 'in', label: 'OE To' },
    { id: 'm', label: 'Month' },
  ];

  const directionOptions = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  // Handlers
  const handleServiceTypesClick = (event: React.MouseEvent<HTMLElement>) => {
    setServiceTypesAnchorEl(event.currentTarget);
  };

  const handleServiceTypesMenuClose = () => {
    setServiceTypesAnchorEl(null);
  };

  const handleGroupByClick = (event: React.MouseEvent<HTMLElement>) => {
    setGroupByAnchorEl(event.currentTarget);
  };

  const handleGroupByMenuClose = () => {
    setGroupByAnchorEl(null);
  };

  const handleFromPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setFromPeriodAnchorEl(event.currentTarget);
  };

  const handleFromPeriodMenuClose = () => {
    setFromPeriodAnchorEl(null);
  };

  const handleFromPeriodChange = (period: string) => {
    customFilters?.onFromPeriodChange?.(period);
    handleFromPeriodMenuClose();
  };

  const handleToPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setToPeriodAnchorEl(event.currentTarget);
  };

  const handleToPeriodMenuClose = () => {
    setToPeriodAnchorEl(null);
  };

  const handleToPeriodChange = (period: string) => {
    customFilters?.onToPeriodChange?.(period);
    handleToPeriodMenuClose();
  };

  const handleServiceTypeToggle = (value: string) => {
    const currentValues = customFilters?.serviceTypes || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    customFilters?.onServiceTypesChange?.(newValues);
  };

  const handleGroupByToggle = (value: string) => {
    const currentValues = customFilters?.groupBy || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    customFilters?.onGroupByChange?.(newValues);
  };

  const handleDirectionChange = (direction: string) => {
    customFilters?.onDirectionChange?.(direction);
  };

  // Display labels
  const getFromPeriodLabel = () => {
    if (!customFilters?.fromPeriod) return 'Select From';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.fromPeriod);
    return option ? option.label : 'Select From';
  };

  const getToPeriodLabel = () => {
    if (!customFilters?.toPeriod) return 'Select To';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.toPeriod);
    return option ? option.label : 'Select To';
  };

  const getServiceTypesLabel = () => {
    const selected = customFilters?.serviceTypes || [];
    if (selected.length === 0) return 'Select Mail Category';
    if (selected.length === serviceTypeOptions.length) return 'All Mail Category';
    return `${selected.length} Mail Category`;
  };

  const getGroupByLabel = () => {
    const selected = customFilters?.groupBy || [];
    if (selected.length === 0) return 'Select Group By';
    if (selected.length === groupByOptions.length) return 'All Group By';
    return `${selected.length} Group By`;
  };

  const getDirectionLabel = () => {
    return customFilters?.direction || 'Inbound';
  };

  return (
    <>
      {/* Direction Filter */}
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
        <StyledSegmentedControl>
          {directionOptions.map((option) => (
            <StyledSegmentButton
              key={option.id}
              active={customFilters?.direction === option.id}
              onClick={() => handleDirectionChange(option.id)}
              sx={{ 
                color: customFilters?.direction === option.id ? '#ffffff' : '#00B6BA',
                minWidth: '70px'
              }}
            >
              {option.label}
            </StyledSegmentButton>
          ))}
        </StyledSegmentedControl>
      </Box>

      {/* From Period Filter */}
      <FilterButton onClick={handleFromPeriodClick}>
        <span className='label'>From YYYY.QQ</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getFromPeriodLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={fromPeriodAnchorEl}
        open={isFromPeriodMenuOpen}
        onClose={handleFromPeriodMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleFromPeriodChange('')}>
          <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
        </MenuItem>
        {yearQuarterOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleFromPeriodChange(option.value)}
            selected={customFilters?.fromPeriod === option.value}
            sx={{
              fontWeight: customFilters?.fromPeriod === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* To Period Filter */}
      <FilterButton onClick={handleToPeriodClick}>
        <span className='label'>To YYYY.QQ</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getToPeriodLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={toPeriodAnchorEl}
        open={isToPeriodMenuOpen}
        onClose={handleToPeriodMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleToPeriodChange('')}>
          <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
        </MenuItem>
        {yearQuarterOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleToPeriodChange(option.value)}
            selected={customFilters?.toPeriod === option.value}
            sx={{
              fontWeight: customFilters?.toPeriod === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Mail Category Filter */}
      <FilterButton onClick={handleServiceTypesClick}>
        <span className='label'>Mail Category</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getServiceTypesLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={serviceTypesAnchorEl}
        open={isServiceTypesMenuOpen}
        onClose={handleServiceTypesMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '250px',
          },
        }}
      >
        {serviceTypeOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleServiceTypeToggle(option.id)}
            sx={{
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              padding: '4px 16px',
            }}
          >
            <Checkbox
              checked={customFilters?.serviceTypes?.includes(option.id) || false}
              sx={{ mr: 1, padding: '4px' }}
            />
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Group By Filter */}
      <FilterButton onClick={handleGroupByClick}>
        <span className='label'>Group By</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getGroupByLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={groupByAnchorEl}
        open={isGroupByMenuOpen}
        onClose={handleGroupByMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '200px',
          },
        }}
      >
        {groupByOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleGroupByToggle(option.id)}
            sx={{
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              padding: '4px 16px',
            }}
          >
            <Checkbox
              checked={customFilters?.groupBy?.includes(option.id) || false}
              sx={{ mr: 1, padding: '4px' }}
            />
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Export Buttons */}
      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
        <StyledActionButton
          onClick={() => customFilters?.onExport?.('HTML')}
          disabled={customFilters?.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters?.isExporting ? <CircularProgress size={16} color="inherit" /> : 'HTML'}
          </Typography>
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters?.onExport?.('PDF')}
          disabled={customFilters?.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters?.isExporting ? <CircularProgress size={16} color="inherit" /> : 'PDF'}
          </Typography>
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters?.onExport?.('XLS')}
          disabled={customFilters?.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters?.isExporting ? <CircularProgress size={16} color="inherit" /> : 'XLS'}
          </Typography>
        </StyledActionButton>
      </Box>
    </>
  );
};

export default CN56ReportFilters; 